import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { CloseOutlined, PlusOutlined, MinusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from '../../../utility/axios';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/modals.css';
import '../../../styles/settings/contacts.css';

const ContactGroupModal = ({ visible, onClose, onAdded, type = 'contact', allowTypeToggle = false, availableContacts = [], editingContact = null }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [modalType, setModalType] = useState(type);
    const [formData, setFormData] = useState({
        email: '',
        firstname: '',
        lastname: '',
        phone: '',
        name: '',
        members: []
    });
    const [memberSearchText, setMemberSearchText] = useState('');
    const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
    const memberInputRef = useRef(null);
    const isEditMode = !!editingContact;

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const filterContacts = (contactList, searchText) => {
        if (!contactList || contactList.length === 0) return [];
        let filtered = contactList.filter(c => c._type !== 'group' && c.contact_type !== 'deleted');
        if (searchText?.trim()) {
            const lower = searchText.toLowerCase();
            filtered = filtered.filter(c => {
                const email = (c.email || '').toLowerCase();
                const name = `${c.firstname || ''} ${c.lastname || ''}`.trim().toLowerCase();
                const phone = (c.phone || '').toLowerCase();
                return email.includes(lower) || name.includes(lower) || phone.includes(lower);
            });
        }
        return [...filtered].sort((a, b) => (a.contact_type === 'favorite' ? 0 : 1) - (b.contact_type === 'favorite' ? 0 : 1));
    };

    const filteredAddContacts = useMemo(() =>
        filterContacts(availableContacts, memberSearchText),
        [availableContacts, memberSearchText]
    );

    const isMemberInGroup = (email) =>
        formData.members.some(m => m.email?.toLowerCase() === email?.toLowerCase());

    const addAsNewOption = useMemo(() => {
        const trimmed = memberSearchText.trim();
        if (!trimmed || !isValidEmail(trimmed)) return null;
        const exists = formData.members.some(m => m.email?.toLowerCase() === trimmed.toLowerCase());
        if (exists) return null;
        const inContacts = filteredAddContacts.some(c => c.email?.toLowerCase() === trimmed.toLowerCase());
        if (inContacts) return null;
        return trimmed;
    }, [memberSearchText, formData.members, filteredAddContacts]);

    // Reset form when modal opens/closes; sync type; prefill when editing
    useEffect(() => {
        if (!visible) {
            setFormData({
                email: '',
                firstname: '',
                lastname: '',
                phone: '',
                name: '',
                members: []
            });
            setModalType(type);
        } else if (editingContact) {
            const isGroup = editingContact._type === 'group';
            const members = isGroup
                ? (editingContact.members
                    ? editingContact.members.map(m => ({
                        email: m.email || m,
                        firstname: m.firstname || '',
                        lastname: m.lastname || '',
                        phone: m.phone || ''
                    }))
                    : (editingContact.email || '').split(';').map(e => e.trim()).filter(Boolean).map(email => ({ email, firstname: '', lastname: '', phone: '' })))
                : [];
            setFormData({
                email: editingContact.email || '',
                firstname: editingContact.firstname || '',
                lastname: (editingContact.lastname || '').trim(),
                phone: (editingContact.phone || '').trim(),
                name: editingContact.group_name || editingContact.firstname || '',
                members
            });
        }
        setMemberSearchText('');
        setMemberDropdownOpen(false);
    }, [visible, editingContact, type]);


    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleMemberChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            members: prev.members.map((m, i) => i === index ? { ...m, [field]: value } : m)
        }));
    };

    const addMember = (member) => {
        const exists = formData.members.some(m => m.email?.toLowerCase() === member.email?.toLowerCase());
        if (exists) return;
        setFormData(prev => ({ ...prev, members: [...prev.members, member] }));
    };

    const removeMemberByEmail = (email) => {
        setFormData(prev => ({
            ...prev,
            members: prev.members.filter(m => m.email?.toLowerCase() !== email?.toLowerCase())
        }));
    };

    const handleAddFromContact = (contact) => {
        addMember({
            email: contact.email,
            firstname: contact.firstname || '',
            lastname: contact.lastname || '',
            phone: contact.phone || ''
        });
    };

    const handleAddFromEmail = (email) => {
        const trimmed = email.trim();
        if (!trimmed || !isValidEmail(trimmed)) return;
        addMember({ email: trimmed, firstname: '', lastname: '', phone: '' });
    };

    const handleRemoveMember = (index) => {
        setFormData(prev => ({
            ...prev,
            members: prev.members.filter((_, i) => i !== index)
        }));
    };

    const updateContactsApi = (payload) => axios.post('/api/profile/contacts/update/', payload);

    const handleSubmit = async () => {
        if (isEditMode) {
            const isGroup = editingContact._type === 'group';
            if (isGroup) {
                if (!formData.name) {
                    message.error(t('contacts.groupNameRequired'));
                    return;
                }
                if (formData.members.length < 2) {
                    message.error(t('contacts.groupMinimumMembers'));
                    return;
                }
                const invalidEmails = formData.members.filter(m => !isValidEmail(m.email));
                if (invalidEmails.length > 0) {
                    message.error(t('contacts.invalidEmails', { emails: invalidEmails.map(m => m.email).join(', ') }));
                    return;
                }
                setLoading(true);
                try {
                    const members = formData.members.map(m => ({
                        email: m.email,
                        firstname: m.firstname || '',
                        lastname: m.lastname || '',
                        phone: m.phone || ''
                    }));
                    await updateContactsApi({
                        group_id: editingContact.id,
                        action: 'update',
                        group_name: formData.name,
                        members
                    });
                    message.success(t('contacts.contactUpdated'));
                    if (onAdded) onAdded();
                    onClose();
                } catch (error) {
                    console.error('Error updating group:', error);
                    message.error(error.response?.data?.error || t('contacts.failedToUpdate'));
                } finally {
                    setLoading(false);
                }
            } else {
                if (!formData.email) {
                    message.error(t('contacts.emailRequired'));
                    return;
                }
                setLoading(true);
                try {
                    await updateContactsApi({
                        ids: [editingContact.id],
                        action: 'update',
                        firstname: formData.firstname,
                        lastname: formData.lastname,
                        email: formData.email,
                        phone: formData.phone
                    });
                    message.success(t('contacts.contactUpdated'));
                    if (onAdded) onAdded();
                    onClose();
                } catch (error) {
                    console.error('Error updating contact:', error);
                    message.error(error.response?.data?.error || t('contacts.failedToUpdate'));
                } finally {
                    setLoading(false);
                }
            }
            return;
        }

        const effectiveType = allowTypeToggle ? modalType : type;
        if (effectiveType === 'contact') {
            // Validation for contact
            if (!formData.email) {
                message.error(t('contacts.emailRequired'));
                return;
            }
            if (!isValidEmail(formData.email)) {
                message.error(t('contacts.invalidEmails', { emails: formData.email }));
                return;
            }

            setLoading(true);
            try {
                const response = await axios.post('/api/profile/contacts/add/', {
                    email: formData.email,
                    firstname: formData.firstname,
                    lastname: formData.lastname,
                    phone: formData.phone
                });
                message.success(t('contacts.contactAdded'));
                
                if (onAdded) {
                    onAdded(response.data.contact, 'contact');
                }
                
                onClose();
            } catch (error) {
                console.error('Error creating contact:', error);
                message.error(error.response?.data?.error || t('contacts.failedToAdd'));
            } finally {
                setLoading(false);
            }
        } else {
            // Validation for group
            if (!formData.name) {
                message.error(t('contacts.groupNameRequired'));
                return;
            }

            // Check if at least one member is added
            if (formData.members.length === 0) {
                message.error(t('contacts.groupMembersRequired'));
                return;
            }

            // Check minimum number of members for a group
            if (formData.members.length < 2) {
                message.error(t('contacts.groupMinimumMembers'));
                return;
            }

            const invalidEmails = formData.members.filter(m => !isValidEmail(m.email));
            if (invalidEmails.length > 0) {
                message.error(t('contacts.invalidEmails', { emails: invalidEmails.map(m => m.email).join(', ') }));
                return;
            }

            setLoading(true);
            try {
                const response = await axios.post('/api/profile/contacts/add/', {
                    group_name: formData.name,
                    members: formData.members.map(m => ({
                        email: m.email,
                        firstname: m.firstname || '',
                        lastname: m.lastname || '',
                        phone: m.phone || ''
                    }))
                });
                message.success(t('contacts.groupAdded'));
                
                if (onAdded) {
                    onAdded(response.data.contact_group, 'group');
                }
                
                onClose();
            } catch (error) {
                console.error('Error creating group:', error);
                message.error(error.response?.data?.error || t('contacts.failedToAddGroup'));
            } finally {
                setLoading(false);
            }
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const isContact = (allowTypeToggle ? modalType : type) === 'contact' || (isEditMode && editingContact?._type === 'contact');
    const isGroup = (allowTypeToggle ? modalType : type) === 'group' || (isEditMode && editingContact?._type === 'group');

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            title={allowTypeToggle && !isEditMode ? (
                <div className="contact-add-type-toggle">
                    <button
                        type="button"
                        className={`contact-add-toggle-btn ${modalType === 'contact' ? 'active' : ''}`}
                        onClick={() => setModalType('contact')}
                    >
                        {t('contacts.newContact')}
                    </button>
                    <button
                        type="button"
                        className={`contact-add-toggle-btn ${modalType === 'group' ? 'active' : ''}`}
                        onClick={() => setModalType('group')}
                    >
                        {t('contacts.newGroup')}
                    </button>
                </div>
            ) : (isEditMode ? t('contacts.editContact') : (isContact ? t('contacts.addContact') : t('contacts.addGroup')))}
            closeIcon={<CloseOutlined />}
            width={560}
            className="settings-modal contact-add-modal"
            centered
        >
            <div className="contact-add-modal-content">
                <div className="contact-add-form">
                    {isContact && (
                        <div className="contact-add-fields">
                            <Input
                                placeholder={t('contacts.emailPlaceholder')}
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className="contact-add-input"
                                type="email"
                                readOnly={isEditMode}
                            />
                            <div className="contact-add-form-row">
                                <Input
                                    placeholder={t('contacts.firstNamePlaceholder')}
                                    value={formData.firstname}
                                    onChange={(e) => handleInputChange('firstname', e.target.value)}
                                    className="contact-add-input"
                                />
                                <Input
                                    placeholder={t('contacts.lastNamePlaceholder')}
                                    value={formData.lastname}
                                    onChange={(e) => handleInputChange('lastname', e.target.value)}
                                    className="contact-add-input"
                                />
                            </div>
                            <Input
                                placeholder={t('contacts.phonePlaceholder')}
                                value={formData.phone}
                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                className="contact-add-input"
                            />
                        </div>
                    )}

                    {isGroup && (
                        <div className="contact-add-fields">
                            <Input
                                placeholder={t('contacts.groupNamePlaceholder')}
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                className="contact-add-input"
                            />
                            <div className="contact-add-members-section">
                                <div className="contact-add-member-add contact-add-member-add-combined">
                                    <div className="contact-add-member-input-wrap">
                                        <Input
                                            ref={memberInputRef}
                                            placeholder={t('contacts.addFromContactsOrEmail')}
                                            value={memberSearchText}
                                            onChange={(e) => {
                                                setMemberSearchText(e.target.value);
                                                setMemberDropdownOpen(true);
                                            }}
                                            onFocus={(e) => { setMemberDropdownOpen(true); e.target.removeAttribute('readonly'); }}
                                            onBlur={() => setTimeout(() => setMemberDropdownOpen(false), 150)}
                                            autoComplete="new-password"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck={false}
                                            data-lpignore="true"
                                            data-form-type="other"
                                            data-1p-ignore
                                            name="contacts-add-member-search"
                                            readOnly
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && memberSearchText.trim()) {
                                                    const trimmed = memberSearchText.trim();
                                                    if (isValidEmail(trimmed)) {
                                                        e.preventDefault();
                                                        handleAddFromEmail(trimmed);
                                                    } else {
                                                        const match = filteredAddContacts[0];
                                                        if (match) {
                                                            e.preventDefault();
                                                            if (isMemberInGroup(match.email)) {
                                                                removeMemberByEmail(match.email);
                                                            } else {
                                                                handleAddFromContact(match);
                                                            }
                                                        }
                                                    }
                                                }
                                            }}
                                            className="contact-add-input"
                                        />
                                        {(filteredAddContacts.length > 0 || addAsNewOption) && memberDropdownOpen && (
                                            <div className="contact-add-member-dropdown">
                                                {filteredAddContacts.map(contact => {
                                                    const inGroup = isMemberInGroup(contact.email);
                                                    return (
                                                        <div
                                                            key={contact.email}
                                                            className={`contact-item ${inGroup ? 'contact-item-in-group' : ''}`}
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                if (inGroup) removeMemberByEmail(contact.email);
                                                                else handleAddFromContact(contact);
                                                            }}
                                                        >
                                                            <span className="contact-item-content">
                                                                {contact.contact_type === 'favorite' && <span className="contact-item-favorite-icon">♥</span>}
                                                                {contact.firstname || contact.lastname
                                                                    ? `${(contact.firstname || '')} ${(contact.lastname || '')}`.trim() + ` (${contact.email})`
                                                                    : contact.email}
                                                            </span>
                                                            <span className="contact-item-actions" onClick={e => e.stopPropagation()}>
                                                                <span className="contact-item-action" onMouseDown={(e) => { e.preventDefault(); inGroup ? removeMemberByEmail(contact.email) : handleAddFromContact(contact); }}>
                                                                    {inGroup ? <MinusOutlined /> : <PlusOutlined />}
                                                                </span>
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                {addAsNewOption && (
                                                    <div
                                                        className="contact-item contact-item-add-new"
                                                        onMouseDown={(e) => { e.preventDefault(); handleAddFromEmail(addAsNewOption); }}
                                                    >
                                                        <span className="contact-item-content">
                                                            {t('contacts.addAsNewMember', { email: addAsNewOption })}
                                                        </span>
                                                        <PlusOutlined className="contact-item-action" />
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {formData.members.length > 0 && (
                                    <div className="contact-add-members-email-blocks">
                                        {formData.members.map((member, index) => (
                                            <div key={index} className="contact-add-member-email-block">
                                                <span className="contact-add-member-email-block-value">{member.email}</span>
                                                <span className="contact-add-member-email-block-remove" onClick={() => handleRemoveMember(index)} title={t('contacts.remove')}>
                                                    <DeleteOutlined />
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="contact-add-modal-footer">
                    <Button
                        type="primary"
                        onClick={handleSubmit}
                        loading={loading}
                        size="middle"
                        className="contact-add-create-btn"
                    >
                        {isEditMode ? t('contacts.saveChanges') : (isContact ? t('contacts.addContact') : t('contacts.addGroup'))}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ContactGroupModal;

